import './answer.scss'

import { List, Table } from '@chakra-ui/react'
import React, { ReactNode, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import rehypeRaw from 'rehype-raw'
import remarkCustomHeaderId from 'remark-custom-header-id'
import remarkDirective from 'remark-directive'
import remarkGfm from 'remark-gfm'

import Copy from '@/components/copy/Copy.tsx'
import { Link } from '@/components/ui/link'
import { Heading, Text } from '@/components/ui/typography'
import useCustomToast from '@/hooks/toasts.tsx'
import useColorModeValue from '@/hooks/useColorModeValue'

import MermaidChart from '../Mermaid'

interface ICodeProps {
    children: ReactNode
    lang: string
}

const CodeBlock = ({ children, lang }: ICodeProps) => {
    const codeRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const text = t('global.copy.toastMessage')
    const [addToast] = useCustomToast()

    return (
        <div className="code-container" ref={codeRef}>
            <Copy
                className="code-copy"
                callback={() => {
                    addToast('success', text)
                }}
                message={String(children).trim()}
                itemHtml={codeRef?.current?.querySelector('div')?.outerHTML}
            />
            <SyntaxHighlighter
                children={String(children).replace(/\n$/, '')}
                style={useColorModeValue(oneLight, oneDark)}
                language={lang}
                PreTag="div"
            />
        </div>
    )
}

const AnswerMarkDown = React.memo(({ answer }: { answer: string | null }) => {
    return (
        <>
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkDirective, remarkCustomHeaderId]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    code({ className, children, ...props }) {
                        const matchMermaid = /mermaid([\s\S]*?)/.exec(className || '')
                        const match = /language-(\w+)/.exec(className || '')
                        if (matchMermaid) {
                            return <MermaidChart chart={children as string} />
                        } else if (match) {
                            return <CodeBlock children={children} lang={match[1]} />
                        } else {
                            return (
                                <code {...props} className={className}>
                                    {children}
                                </code>
                            )
                        }
                    },
                    table({ children }) {
                        return (
                            <Table.Root colorScheme="orange" overflowWrap="break-word" maxW="100%" variant="gsktable">
                                {children}
                            </Table.Root>
                        )
                    },
                    thead({ children }) {
                        return <Table.Header>{children}</Table.Header>
                    },
                    tbody({ children }) {
                        return <Table.Body>{children}</Table.Body>
                    },
                    tr({ children }) {
                        return <Table.Row>{children}</Table.Row>
                    },
                    th({ children }) {
                        return <Table.ColumnHeader>{children}</Table.ColumnHeader>
                    },
                    td({ children }) {
                        return <Table.Cell>{children}</Table.Cell>
                    },
                    ul({ children, ...props }) {
                        return (
                            <List.Root variant="marker" listStylePosition="inside" marginInlineStart="0" {...props}>
                                {children}
                            </List.Root>
                        )
                    },
                    ol({ children, ...props }) {
                        return (
                            <List.Root as="ol" variant="marker" listStylePosition="inside" marginInlineStart="0" {...props}>
                                {children}
                            </List.Root>
                        )
                    },
                    li({ children, ...props }) {
                        return <List.Item {...props}>{children}</List.Item>
                    },
                    a(props) {
                        return (
                            <Link
                                isExternal={props.href && props.href.startsWith('#') ? false : true}
                                textDecoration="underline"
                                fontSize="14px"
                                letterSpacing="0.25px"
                                lineHeight="20px"
                                href={props.href}
                            >
                                {props.children}
                            </Link>
                        )
                    },
                    h1({ children, ...props }) {
                        return (
                            <Heading as="h1" fontSize="37px" fontWeight="bold" lineHeight="56px" {...props}>
                                {children}
                            </Heading>
                        )
                    },
                    h2({ children, ...props }) {
                        return (
                            <Heading as="h2" fontSize="28px" letterSpacing="0.25px" lineHeight="44px" {...props}>
                                {children}
                            </Heading>
                        )
                    },
                    h3({ children, ...props }) {
                        return (
                            <Heading as="h3" fontSize="21px" fontWeight="bold" letterSpacing="0.25px" lineHeight="36px" {...props}>
                                {children}
                            </Heading>
                        )
                    },
                    h4({ children, ...props }) {
                        return (
                            <Heading as="h4" fontSize="16px" fontWeight="bold" lineHeight="31px" {...props}>
                                {children}
                            </Heading>
                        )
                    },
                    h5({ children, ...props }) {
                        return (
                            <Heading as="h5" fontSize="16px" letterSpacing="0.25px" lineHeight="31px" {...props}>
                                {children}
                            </Heading>
                        )
                    },
                    h6({ children, ...props }) {
                        return (
                            <Heading as="h6" fontSize="14px" fontWeight="bold" letterSpacing="0.25px" lineHeight="29px" {...props}>
                                {children}
                            </Heading>
                        )
                    },
                    blockquote({ children }) {
                        return (
                            <Text
                                borderLeft="3px solid"
                                borderLeftColor="var(--chakra-colors-surface-dark)"
                                paddingLeft="16px"
                                paddingTop="16px"
                                paddingBottom="16px"
                                fontSize="13px"
                                letterSpacing="0.25px"
                                fontStyle="italic"
                            >
                                {children}
                            </Text>
                        )
                    },
                }}
                children={answer || ''}
            />
        </>
    )
})

export default AnswerMarkDown
